export const learnIt = {
  meta: {
    title: "Imparare ad imparare — Lumen",
    description: "La guida interattiva per capire come funziona la memoria, il cervello e i migliori metodi di studio.",
  },
  header: {
    badge: "Guida",
    title: "Imparare ad imparare",
    subtitlePre: "Non serve studiare di più. Serve studiare ",
    subtitleBold: "meglio",
    subtitlePost: ". Ecco come funziona davvero la tua mente.",
  },
  science: { heading: "La scienza dello studio" },
  methodsHeading: "I metodi di studio",
  finalTip: {
    title: "Il consiglio finale",
    body: "Non esiste il metodo perfetto per tutti. Prova, misura, adatta. Il tuo tutor AI ti aiuterà a scegliere il metodo giusto per ogni materia.",
  },
  lvl: { easy: "Facile", media: "Media" },
  badge: { accessible: "Accessibile" },
  methodsList: { when: "Quando:" },
  dialog: {
    howTo: "Come si fa",
    example: "Esempio pratico",
    adaptationsTitle: "Varianti accessibili",
    adaptationsFor: "Adattamenti per",
    noAdaptations: "Nessuna variante specifica: il metodo base è già adatto.",
  },
  adaptMeta: {
    dsa: { label: "DSA", short: "dislessia, disortografia, discalculia" },
    adhd: { label: "ADHD", short: "attenzione e iperattività" },
    bes: { label: "BES", short: "bisogni educativi speciali" },
    focus: { label: "Concentrazione", short: "difficoltà a stare focalizzati" },
  },
  chapters: {
    memory: {
      title: "Come funziona la memoria",
      body: "La memoria non è un cassetto: è un muscolo. Ogni volta che RICHIAMI un'informazione — invece di rileggerla — la fissi più a fondo. Questo si chiama **Active Recall**.\n\nLa curva dell'oblio (Ebbinghaus) mostra che dopo 24h ricordiamo il 30% di ciò che abbiamo letto passivamente. Ripassando in modo distribuito (spaced repetition), la ritenzione sale all'80–90%.",
    },
    brain: {
      title: "Come funziona il cervello quando studia",
      body: "Il cervello ha due modalità: **focused** (concentrata) e **diffuse** (rilassata). Alternarle — con pause e sonno — è ciò che permette all'apprendimento di consolidarsi. Studiare per 8h di fila senza pause è controproducente.",
    },
    mistakes: {
      title: "Errori più comuni",
      body: "• **Rileggere** invece di ripetere ad alta voce.\n• Studiare **passivo** con sottolineature colorate senza mai richiamare.\n• Studiare tutto insieme all'ultimo momento (cramming).\n• Non dormire abbastanza: senza sonno REM la memoria non consolida.",
    },
    notes: {
      title: "Come prendere appunti",
      body: "Il metodo **Cornell**: dividi la pagina in 3 zone (note, parole chiave, riassunto). Alla fine della lezione, chiudi il libro e riempi il riassunto a memoria. Aumenta la ritenzione fino al 34%.",
    },
    anxiety: {
      title: "Come gestire l'ansia da verifica",
      body: "La respirazione 4-7-8 (inspira 4s, trattieni 7s, espira 8s) abbassa il cortisolo. Il giorno prima non studiare argomenti nuovi: **ripassa** solo. La sicurezza nasce dalla preparazione, non dall'ansia.",
    },
    forgetting: {
      title: "Come evitare di dimenticare",
      body: "Ripassa un argomento dopo 1 giorno, 3 giorni, 7 giorni, 21 giorni. Ogni richiamo riparte più forte. È il segreto degli studenti di medicina.",
    },
  },
  methods: {
    feynman: {
      d: "Spiega come a un bambino di 10 anni.",
      when: "Quando devi capire davvero un concetto.",
      how: [
        "Scegli un concetto e scrivilo in cima a un foglio.",
        "Spiegalo a voce come se parlassi a un bambino di 10 anni.",
        "Segna dove ti blocchi o usi parole difficili.",
        "Torna al libro solo su quei punti, poi rispiega da capo.",
      ],
      example: "Concetto: la fotosintesi.\n\nSpiegazione da bambino: \"Le piante mangiano la luce. Prendono acqua dalle radici e aria dalle foglie, e con la luce del sole cucinano lo zucchero per crescere. L'aria che noi respiriamo è il loro 'scarto'.\"\n\nBlocco tipico: non sai dire cosa c'entra la clorofilla. → Torni al libro, scopri che è la molecola verde che cattura la luce, e rispieghi: \"La clorofilla è come un pannello solare dentro la foglia.\"",
      tip: "Se non riesci a spiegarlo semplice, non l'hai capito. Ancora.",
      adaptations: {
        dsa: [
          "Registra a voce la spiegazione invece di scriverla — puoi riascoltarti.",
          "Usa il tutor AI dell'app per riscrivere il concetto in parole più semplici.",
          "Font ad alta leggibilità e attiva la modalità DSA nelle impostazioni.",
        ],
        adhd: [
          "Un solo concetto alla volta, timer visibile da 10 minuti.",
          "Spiega camminando o gesticolando: il movimento aiuta a mantenere il focus.",
          "Ricompensa immediata alla fine (5 min musica, snack, break).",
        ],
        bes: [
          "Usa disegni o icone al posto delle parole difficili.",
          "Chiedi a un compagno/genitore di ascoltarti: il feedback riduce l'ansia.",
          "Suddividi la spiegazione in 3 mini-parti da 2 minuti l'una.",
        ],
        focus: [
          "Elimina distrazioni: telefono in un'altra stanza, notifiche off.",
          "Spiega ad alta voce come se stessi facendo un video: attiva più attenzione.",
        ],
      },
    },
    activeRecall: {
      d: "Chiudi il libro. Ripeti a voce.",
      when: "Sempre. È il metodo #1.",
      how: [
        "Leggi un paragrafo o una pagina.",
        "Chiudi il libro.",
        "Ripeti a voce (o scrivi) tutto ciò che ricordi.",
        "Riapri e correggi gli errori.",
      ],
      example: "Storia — capitolo sulla Rivoluzione Francese.\n\n1) Leggi le cause (1789).\n2) Chiudi il libro e prova a dire: \"Crisi economica, disuguaglianza fra i tre stati, influenza dell'Illuminismo, presa della Bastiglia il 14 luglio…\"\n3) Riapri: hai dimenticato la carestia del 1788. Segnala in rosso.\n4) Riprova dopo 10 minuti — questa volta la ricordi.",
      tip: "Rileggere ≠ studiare. Se non lo richiami, non lo sai.",
      adaptations: {
        dsa: [
          "Ripeti a voce alta invece di scrivere: aggira lettura e scrittura faticose.",
          "Usa audio-flashcard: registra la domanda e ascoltala prima di rispondere.",
          "Chiedi al tutor AI di farti domande orali su ciò che hai studiato.",
        ],
        adhd: [
          "Sessioni brevi da 5-10 minuti, poi cambia argomento per non annoiarti.",
          "Trasforma il ripasso in un gioco a punti con te stesso.",
          "In piedi o camminando: il movimento aiuta il richiamo.",
        ],
        bes: [
          "Parti da 3 domande semplici prima di aumentare la difficoltà.",
          "Usa immagini-guida per attivare il ricordo (memoria visiva).",
        ],
        focus: [
          "Timer breve (10 min) e obiettivo chiaro: \"ripeto 5 punti\".",
          "Scrivi la risposta di getto senza rileggere: attiva subito il richiamo.",
        ],
      },
    },
    pomodoro: {
      d: "25 min focus + 5 min pausa.",
      when: "Per iniziare a studiare senza rimandare.",
      how: [
        "Scegli UN compito preciso (non 'studia storia', ma 'leggi pag. 45–52').",
        "Timer 25 min. Telefono in un'altra stanza.",
        "5 min di pausa vera (alzati, bevi, guarda fuori).",
        "Dopo 4 pomodori: pausa lunga 15–30 min.",
      ],
      example: "Ore 15:00 — Pomodoro 1: leggo pagg. 45–52 di storia.\n15:25 — Pausa: cammino in cucina, bevo acqua.\n15:30 — Pomodoro 2: chiudo il libro e faccio Active Recall del capitolo.\n15:55 — Pausa.\n16:00 — Pomodoro 3: risolvo 5 esercizi di matematica.\n16:25 — Pausa lunga 20 min.",
      tip: "Usa il timer dell'app Studio → modalità Pomodoro.",
      adaptations: {
        dsa: [
          "Durante il pomodoro alterna 10 min lettura + 15 min attività orale/visiva.",
          "Nella pausa non leggere: riposa gli occhi (sguardo lontano, luce naturale).",
        ],
        adhd: [
          "Riduci a **15 min focus + 5 min pausa** — più realistico per l'ADHD.",
          "Timer visibile e sonoro. Scrivi il compito su un post-it davanti a te.",
          "Se ti distrai, non giudicarti: nota il pensiero, torna al compito.",
        ],
        bes: [
          "Parti con 1 solo pomodoro al giorno, poi cresci gradualmente.",
          "Usa un timer analogico o clessidra: il tempo diventa concreto.",
        ],
        focus: [
          "Modalità aereo obbligatoria durante i 25 minuti.",
          "Pausa VERA: no social, no video. Solo movimento o acqua.",
        ],
      },
    },
    spacedRepetition: {
      d: "Ripassa a intervalli crescenti.",
      when: "Per ricordare a lungo termine.",
      how: [
        "Studia oggi (giorno 0).",
        "Ripassa dopo 1 giorno.",
        "Poi dopo 3 giorni, 7 giorni, 21 giorni.",
        "Ogni volta che sbagli, riparti da 1 giorno.",
      ],
      example: "Vocaboli di inglese, 20 parole nuove il lunedì.\n\n• Mar (giorno 1): richiami le 20 parole. Sbagli 4 → segnale.\n• Gio (giorno 3): richiami tutte. Le 4 sbagliate le riporti al giorno 1.\n• Lun successivo (giorno 7): rivedi solo quelle stabili.\n• Fra 3 settimane: ripasso finale, 5 minuti.\n\nRisultato: 90% di ritenzione dopo un mese, vs 30% con solo rilettura.",
      tip: "Le flashcard dell'app usano già questo metodo (5 box Leitner).",
      adaptations: {
        dsa: [
          "Preferisci flashcard con immagini o audio, non solo testo.",
          "Nell'app puoi creare flashcard vocali: registri la risposta a voce.",
        ],
        adhd: [
          "Massimo 10 carte per sessione: mazzi piccoli evitano l'abbandono.",
          "Fai il ripasso sempre alla stessa ora: la routine aiuta più della forza di volontà.",
        ],
        bes: [
          "Usa colori diversi per materia: aiuta a ritrovare le carte giuste.",
          "Chiedi supporto per creare le prime carte, poi diventerà autonomo.",
        ],
        focus: [
          "10 minuti al mattino appena sveglio: il cervello è più recettivo.",
          "Chiudi tutte le altre schede/app prima di iniziare il mazzo.",
        ],
      },
    },
    cornellNotes: {
      d: "Appunti in 3 zone: note / parole / riassunto.",
      when: "Durante lezioni frontali.",
      how: [
        "Dividi la pagina in 3 zone: colonna sx (parole chiave), colonna dx (note), banda in basso (riassunto).",
        "Durante la lezione scrivi solo nella colonna note (dx).",
        "Subito dopo, riempi le parole chiave a sinistra.",
        "Entro sera, scrivi 2-3 righe di riassunto in fondo.",
      ],
      example: "Pagina di biologia.\n\n[Note dx] La mitosi ha 4 fasi: profase, metafase, anafase, telofase…\n[Parole sx] mitosi · 4 fasi · cromosomi\n[Riassunto basso] La mitosi divide una cellula in 2 identiche. 4 fasi. Serve per crescita e riparazione.",
      tip: "Coprendo la colonna dx puoi usare le parole chiave come flashcard.",
      adaptations: {
        dsa: [
          "Scrivi in stampatello o usa il tablet con font ad alta leggibilità.",
          "Registra la lezione (con permesso) e riempi le note dopo, con calma.",
          "Nella colonna sx usa simboli/icone al posto delle parole quando puoi.",
        ],
        adhd: [
          "Focus solo sulla colonna dx durante la lezione: una cosa alla volta.",
          "Riempi parole chiave e riassunto SUBITO dopo la lezione, non il giorno dopo.",
        ],
        bes: [
          "Modello prestampato con le 3 zone già divise: riduce lo sforzo organizzativo.",
          "Riassunto in max 2 frasi: meglio breve e completato che lungo e lasciato a metà.",
        ],
        focus: [
          "Penna in mano sempre: scrivere ancora l'attenzione anche se non copi tutto.",
        ],
      },
    },
    blurting: {
      d: "Scrivi tutto ciò che ricordi, poi confronta.",
      when: "Prima di una verifica.",
      how: [
        "Scegli un argomento.",
        "Foglio bianco. 5-10 min: scrivi TUTTO quello che ricordi, senza ordine.",
        "Apri il libro e confronta.",
        "Cerchia in rosso i buchi. Sono la tua prossima sessione di studio.",
      ],
      example: "Argomento: sistema circolatorio.\n\nBlurt: \"Cuore, 4 camere, atrio dx e sx, ventricoli, arterie portano sangue ossigenato tranne polmonare, vene…\"\n\nApri il libro: hai scordato la valvola tricuspide e il ruolo dei capillari. Ecco cosa ripassare stasera.",
      tip: "Il blurting rende visibili i buchi in 10 minuti. Fallo il giorno prima di una verifica.",
      adaptations: {
        dsa: [
          "Fai il blurting a voce: registra un audio invece di scrivere.",
          "Al confronto col libro usa evidenziatori colorati per i \"buchi\".",
        ],
        adhd: [
          "Timer di 5 minuti: quando finisce, stop, anche se non hai finito.",
          "Un solo argomento per volta. Non passare al prossimo prima del confronto.",
        ],
        bes: [
          "Puoi usare schemi/frecce invece di frasi complete.",
          "Il primo blurting è sempre pieno di buchi: è NORMALE, non un fallimento.",
        ],
        focus: [
          "Foglio bianco davanti, tutto il resto lontano. Scrivi di getto senza fermarti.",
        ],
      },
    },
    mindMapping: {
      d: "Rappresenta i concetti in modo visivo.",
      when: "Per collegare idee complesse.",
      how: [
        "Scrivi il concetto centrale al centro di un foglio.",
        "Diramati con 3-5 rami principali (sottotemi).",
        "Ogni ramo si divide in sotto-rami con dettagli, esempi, date.",
        "Usa colori diversi per macro-aree e frecce per collegamenti.",
      ],
      example: "Centro: \"Seconda Guerra Mondiale\".\n\nRami: Cause · Fronti · Personaggi · Conseguenze.\n\n• Cause → Trattato Versailles, crisi 1929, ascesa nazismo.\n• Fronti → Europa, Pacifico, Africa.\n• Personaggi → Hitler, Churchill, Roosevelt, Stalin.\n• Conseguenze → ONU, guerra fredda, decolonizzazione.\n\nFrecce fra 'crisi 1929' e 'ascesa nazismo' per mostrare causa-effetto.",
      tip: "Nell'app puoi generare mappe mentali dai tuoi appunti dalla sezione Studio.",
      adaptations: {
        dsa: [
          "Metodo IDEALE per DSA: sfrutta memoria visiva e riduce il testo.",
          "Usa colori, icone e disegni al posto di frasi lunghe.",
          "Nell'app puoi generare la mappa dagli appunti in un clic.",
        ],
        adhd: [
          "Parti dal centro senza pianificare troppo: aggiungi rami man mano che pensi.",
          "Se ti perdi, ferma tutto e torna al concetto centrale.",
        ],
        bes: [
          "Mappa piccola, max 3 rami principali. Meglio semplice e chiara.",
          "Un colore per ramo aiuta a memorizzare i collegamenti.",
        ],
        focus: [
          "Costruisci la mappa a mano su foglio grande: il gesto tiene svegli.",
          "Fai una mappa alla volta per argomento — non mescolare materie.",
        ],
      },
    },
  },
  toast: {
    mustLogin: "Devi essere collegato per salvare",
    saved: "Risultato salvato nel tuo profilo ✨",
  },
  quiz: {
    cta: {
      discoverTitle: "Scopri il tuo metodo",
      discoverSubtitle: "7 domande veloci per capire quale metodo di studio funziona meglio per te.",
      yourMethod: "Il tuo metodo",
      recommendedBadge: "Consigliato",
      savedOn: "salvato il {{date}}",
      thenPrefix: " · poi ",
      andWord: " e ",
      viewCard: "Vedi scheda",
      retakeTest: "Rifai il test",
    },
    dialogTitle: "Scopri il tuo metodo",
    questionOf: "Domanda {{n}} di {{total}}",
    resultsSubtitle: "Ecco i metodi più adatti a te",
    noResults: "Nessun risultato — riprova.",
    recommendedBadge: "Consigliato",
    retake: "Rifai il test",
    questions: [
      {
        q: "Quando studi qualcosa di nuovo, cosa ti aiuta di più a capirlo?",
        options: [
          "Spiegarlo a voce a qualcuno (o a me stesso)",
          "Vedere schemi, collegamenti e disegni",
          "Ripetere ad alta voce senza guardare il libro",
        ],
      },
      {
        q: "Quanta fatica fai a metterti a studiare?",
        options: [
          "Tantissima, procrastino sempre",
          "Abbastanza, dopo un po' entro nel flusso",
          "Poca, parto subito",
        ],
      },
      {
        q: "Come prendi appunti in classe?",
        options: [
          "Scrivo praticamente tutto",
          "Solo parole chiave",
          "Ascolto, riscrivo a casa",
        ],
      },
      {
        q: "Dopo quanto tempo tendi a dimenticare ciò che hai studiato?",
        options: [
          "Dopo pochi giorni",
          "Dopo una settimana o due",
          "Lo ricordo a lungo",
        ],
      },
      {
        q: "Come ti prepari di solito a una verifica?",
        options: [
          "Ripasso intensivo il giorno prima",
          "Ripasso poco alla volta durante la settimana",
          "Studio tutto la sera prima",
        ],
      },
      {
        q: "Quale attività ti aiuta di più a fissare le informazioni?",
        options: [
          "Scrivere a mano",
          "Parlare, spiegare",
          "Disegnare mappe, schemi, colori",
        ],
      },
      {
        q: "Che tipo di materie ti pesano di più?",
        options: [
          "Definizioni, vocaboli, formule",
          "Concetti astratti da capire",
          "Argomenti collegati (storia, letteratura)",
        ],
      },
    ],
  },
};
